// React
import { useMemo } from "react";

// Third-party
import clsx from "clsx";

// MUI
import { GridColDef, useGridApiRef } from "@mui/x-data-grid-premium";

// Components
import DataSmartTable from "@/Layouts/components/Tables/DataSmartTable";
import NoRowsOverlay from "@/Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";
import CommonToolbar from "@/Layouts/components/Tables/DataSmartTable/toolbar/CommonToolbar";
import { TypographyHeader } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyHeader";
import PhoneNumbersRenderCell from "@/DataPages/components/common/PhoneNumbersRenderCell";
import EmailsRenderCell from "@/DataPages/components/common/EmailsRenderCell";
import TypographyCell from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyCell";
import TableCustomComponents from "@/DataPages/components/common/PaginationHeaderLoading";

// Helpers
import { formatPhoneNumber } from "@/Common/helpers/Modifiers/formatters";
import { getFormatUTCDate } from "@/Common/helpers/Dates";

// Types
import { DataSmartTableProps } from "@/Layouts/components/types";
import { IMailDetails, IPhoneDetails, ISalesLeadsCustomer } from "@/DataPages/types/rci/salesLeads/model";
import { Nullable } from "@/Common/interfaces/types";
import { TSelectOption } from "@/DataPages/types/crm/customers";

// Constants
import { BASE_TABLE_PROPS, DEFAULT_HEADER_HEIGHT, DEFAULT_ROW_HEIGHT, ID_GRID_COLDEF } from "@/Washouts/constants";
import {
	EIGHTY,
	ONE_HUNDRED_EIGHTY,
	ONE_HUNDRED_FORTY,
	ONE_HUNDRED_SEVENTY,
	ONE_HUNDRED_SIXTY,
	ONE_HUNDRED_TWENTY,
	TWO_HUNDRED,
} from "@/Constants/tables";
import { DATE_FORMAT } from "@/Constants/date";

const maxRows = 14;

const slots = {
	toolbar: CommonToolbar,
	noRowsOverlay: NoRowsOverlay,
	noResultsOverlay: NoRowsOverlay,
};

const customProps: DataSmartTableProps["customProps"] = {
	...BASE_TABLE_PROPS,
	tableContainerClassName: clsx("[&_.MuiDataGrid-main]:mt-2 after:[&_.MuiDataGrid-topContainer]:!bg-disabled", {
		"[&_.toolbar-actions]:!absolute [&_.toolbar-actions]:!right-[0px]": true,
	}),
	tableClassName: {
		"[&_.MuiDataGrid-columnHeader--withRightBorder]:!border-none": true,
		"[&_.MuiDataGrid-filler--pinnedLeft]:!border-r-transparent": true,
		"[&_.MuiDataGrid-row:not(.footer):hover_.MuiDataGrid-cell--pinnedLeft:not(.empty-row,.group-child)]:shadow-table-hov": true,
	},
	aggregationFooterClassName: "aggregation-footer regular-footer",
	expandableRows: false,
	hide: false,
};

const columnVisibilityModel = {
	_id: false,
};

const baseColumnDefinition: Partial<GridColDef> = {
	headerClassName: "table-header-bottom-aligned",
	renderCell: (params) => <TypographyCell params={params} />,
	renderHeader: (params) => <TypographyHeader params={params} />,
};

const getBaseColumnDefinition = (width?: number) => {
	if (!width) {
		return baseColumnDefinition;
	}

	return {
		...baseColumnDefinition,
		width,
		minWidth: width,
		maxWidth: width,
	};
};

const columns: GridColDef[] = [
	ID_GRID_COLDEF,
	{
		...getBaseColumnDefinition(EIGHTY),
		field: "ibFlag",
		headerName: "IB Flag",
	},
	{
		...getBaseColumnDefinition(ONE_HUNDRED_SEVENTY),
		field: "customerName",
		headerName: "Customer Name",
		valueGetter: (_, row) => `${row?.firstName ?? ""} ${row?.midName ?? ""} ${row?.lastName ?? ""}`.trim(),
	},
	{
		...getBaseColumnDefinition(ONE_HUNDRED_SIXTY),
		field: "businessName",
		headerName: "Business Name",
	},
	{
		...getBaseColumnDefinition(EIGHTY),
		field: "nameRecId",
		headerName: "DMS ID #",
		align: "right",
	},
	{
		...getBaseColumnDefinition(ONE_HUNDRED_FORTY),
		field: "prefContact",
		headerName: "Pref. Contact",
	},
	{
		...getBaseColumnDefinition(ONE_HUNDRED_FORTY),
		field: "sendTextsTo",
		headerName: "Send Texts To",
	},
	{
		...getBaseColumnDefinition(ONE_HUNDRED_EIGHTY),
		field: "phoneNumbers",
		headerName: "Phone",
		valueGetter: (value) => {
			if (!Array.isArray(value)) {
				return "";
			}
			return (value as IPhoneDetails[]).map((phone) => {
				const type = phone?.type ? `(${phone?.type})` : "";
				const formattedPhone = formatPhoneNumber(phone?.number, false);
				const ext = phone?.extension ? `ext. ${phone?.extension}` : "";
				return `${type} ${formattedPhone} ${ext}`;
			});
		},
		renderCell: (params) => <PhoneNumbersRenderCell params={params} />,
	},
	{
		...getBaseColumnDefinition(TWO_HUNDRED),
		field: "email",
		headerName: "Email",
		valueGetter: (value) => {
			if (!Array.isArray(value)) {
				return "";
			}
			return (value as IMailDetails[]).map((emailItem) => {
				return emailItem?.mailTo;
			});
		},
		renderCell: (params) => {
			return <EmailsRenderCell params={params} />;
		},
	},
	{
		...getBaseColumnDefinition(ONE_HUNDRED_TWENTY),
		field: "createdAt",
		headerName: "Created On",
		type: "dateTime",
		valueFormatter: (value) => getFormatUTCDate(value, DATE_FORMAT.usDateTime12hPadded),
	},
];

interface ICustomersTableProps {
	data: Nullable<ISalesLeadsCustomer[]>;
	hasNextPage: boolean;
	isFetching: boolean;
	selectionOptions: TSelectOption[];
	selectedOption?: TSelectOption;
}

export default function CustomersTable(props: Readonly<ICustomersTableProps>) {
	const { data, hasNextPage, isFetching, selectionOptions, selectedOption } = props;
	const apiRef = useGridApiRef();

	const slotProps = useMemo(
		() => ({
			toolbar: {
				customComponent: (
					<>
						<TableCustomComponents
							hasNextPage={hasNextPage}
							isFetching={isFetching}
							text={"Sales Leads Customers data is still loading..."}
						/>
					</>
				),
				filter: {
					data: data ?? [],
					hide: false,
					disabled: false,
				},
				search: {
					useQuickFilter: true,
				},
				expand: {
					hide: true,
				},
			},
			noRowsOverlay: {
				title: "No Customer Data",
				className: "!text-montserrat",
			},
			noResultsOverlay: {
				title: "No Customer Data",
				className: "!text-montserrat",
			},
		}),
		[data, selectedOption, hasNextPage, isFetching, selectionOptions]
	);

	return (
		<DataSmartTable
			apiRef={apiRef}
			columns={columns}
			rows={data ?? []}
			headerHeight={DEFAULT_HEADER_HEIGHT}
			maxRows={maxRows}
			enableCustomScrollbars
			disableColumnMenu
			disableRowSelectionOnClick
			checkboxSelection
			columnVisibilityModel={columnVisibilityModel}
			slots={slots}
			slotProps={slotProps}
			hideFooter
			customProps={customProps}
			rowHeight={DEFAULT_ROW_HEIGHT}
			autoHeight={false}
		/>
	);
}
